<template>
  <div>
    <div class="space-y-6">
      <!-- 页面标题 -->
      <PageHeader 
        title="系统警报" 
        :breadcrumbs="[
          { text: '首页', link: '#' },
          { text: '系统管理', link: '#' },
          { text: '系统警报', link: null }
        ]"
      />
      
      <!-- 警报概览卡片 -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center">
            <div class="p-3 rounded-full bg-red-100 text-red-600">
              <i class="fas fa-bell text-xl"></i>
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-500">活跃警报</p>
              <p class="text-2xl font-semibold text-gray-800">{{ activeCount }}</p>
            </div>
          </div>
          <div class="mt-4">
            <div class="flex items-center justify-between">
              <p class="text-sm text-gray-500">较昨日</p>
              <p class="text-sm text-red-600 font-semibold">{{ activeCountDiff }}</p>
            </div>
          </div>
        </div>
        
        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center">
            <div class="p-3 rounded-full bg-yellow-100 text-yellow-600">
              <i class="fas fa-exclamation-triangle text-xl"></i>
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-500">严重警报</p>
              <p class="text-2xl font-semibold text-gray-800">{{ criticalCount }}</p>
            </div>
          </div>
          <div class="mt-4">
            <div class="flex items-center justify-between">
              <p class="text-sm text-gray-500">较昨日</p>
              <p class="text-sm text-red-600 font-semibold">{{ criticalCountDiff }}</p>
            </div>
          </div>
        </div>
        
        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center">
            <div class="p-3 rounded-full bg-green-100 text-green-600">
              <i class="fas fa-check-circle text-xl"></i>
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-500">已解决警报</p>
              <p class="text-2xl font-semibold text-gray-800">{{ resolvedCount }}</p>
            </div>
          </div>
          <div class="mt-4">
            <div class="flex items-center justify-between">
              <p class="text-sm text-gray-500">本周</p>
              <p class="text-sm text-green-600 font-semibold">{{ resolvedCountWeek }}</p>
            </div>
          </div>
        </div>
        
        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center">
            <div class="p-3 rounded-full bg-blue-100 text-blue-600">
              <i class="fas fa-clock text-xl"></i>
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-500">平均响应时间</p>
              <p class="text-2xl font-semibold text-gray-800">{{ avgResponseTime }}</p>
            </div>
          </div>
          <div class="mt-4">
            <div class="flex items-center justify-between">
              <p class="text-sm text-gray-500">较上周</p>
              <p class="text-sm text-green-600 font-semibold">{{ avgResponseTimeDiff }}</p>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 筛选器 -->
      <Card>
        <SearchFilters
          :filter-config="filterConfig"
          :initial-filters="filters"
          :date-picker-options="{
            mode: 'range',
            dateFormat: 'Y-m-d',
            locale: 'zh',
            allowInput: true
          }"
          @search="searchAlerts"
          @reset="resetFilters"
        />
      </Card>

      <!-- 警报趋势图表 -->
      <Card>
        <div class="px-6 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center">
          <h3 class="text-lg leading-6 font-medium text-gray-900">警报趋势（近7天）</h3>
          <div class="flex space-x-3">
            <div class="flex bg-gray-100 rounded-lg p-1 shadow-inner mr-2">
              <button 
                type="button" 
                @click="showSeverityTrend"
                :class="[
                  'py-2 px-4 rounded-md text-sm font-medium transition-all duration-200',
                  showingSeverityTrend ? 'bg-blue-500 text-white shadow-sm' : 'text-gray-700'
                ]"
              >
                <i class="fas fa-exclamation-triangle mr-2"></i>按严重程度
              </button>
              <button 
                type="button" 
                @click="showStatusTrend"
                :class="[
                  'py-2 px-4 rounded-md text-sm font-medium transition-all duration-200',
                  !showingSeverityTrend ? 'bg-blue-500 text-white shadow-sm' : 'text-gray-700'
                ]"
              >
                <i class="fas fa-tasks mr-2"></i>按状态
              </button>
            </div>
            <button type="button" @click="downloadChart" class="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200">
              <i class="fas fa-download mr-2"></i>下载图表
            </button>
          </div>
        </div>
        <div class="p-6">
          <div v-show="showingSeverityTrend" class="h-96 w-full fade-in">
            <div class="mb-3 text-sm text-gray-500 border-l-4 border-blue-500 pl-3 py-1 bg-blue-50 rounded-r-md">
              <span class="inline-flex items-center mr-4">
                <i class="fas fa-info-circle mr-2 text-blue-500"></i>
                显示近7天按严重程度划分的警报数量趋势
              </span>
            </div>
            <div class="bg-white p-4 rounded-lg shadow-inner h-full chart-container">
              <canvas ref="alertsTrendChart" class="h-full w-full"></canvas>
            </div>
          </div>
          <div v-show="!showingSeverityTrend" class="h-96 w-full fade-in">
            <div class="mb-3 text-sm text-gray-500 border-l-4 border-green-500 pl-3 py-1 bg-green-50 rounded-r-md">
              <span class="inline-flex items-center mr-4">
                <i class="fas fa-info-circle mr-2 text-green-500"></i>
                显示近7天按状态划分的警报数量变化趋势
              </span>
            </div>
            <div class="bg-white p-4 rounded-lg shadow-inner h-full chart-container">
              <canvas ref="statusTrendChart" class="h-full w-full"></canvas>
            </div>
          </div>
        </div>
      </Card>
      
      <!-- 警报列表 -->
      <Card>
        <div class="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center">
          <h3 class="text-lg leading-6 font-medium text-gray-900">系统警报列表</h3>
          <div class="flex space-x-3">
            <button type="button" @click="pauseAllAlerts" class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-500 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
              <i class="fas fa-bell-slash mr-2"></i> 暂停所有警报
            </button>
          </div>
        </div>
        
        <TableList
          :columns="alertColumns"
          :items="alerts"
          :loading="loading"
          :total-items="totalItems"
          :current-page="currentPage"
          :items-per-page="itemsPerPage"
          empty-text="没有找到符合条件的警报记录"
          @page-change="goToPage"
        >
          <!-- 自定义渲染严重程度列 -->
          <template #cell-severity="{ item }">
            <span v-if="item && item.severity" :class="getSeverityClasses(item.severity)" class="px-2 py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full">
              <i :class="getSeverityIcon(item.severity)" class="mr-1"></i>
              {{ getSeverityText(item.severity) }}
            </span>
            <span v-else class="px-2 py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full bg-gray-200 text-gray-900">
              <i class="fas fa-info-circle mr-1"></i>未知
            </span>
          </template>
          
          <template #cell-content="{ item }">
            <div v-if="item" class="text-sm text-gray-900 truncate max-w-xs" :title="(item.title || '') + (item.message ? ' - ' + item.message : '')">
              {{ item.title || '' }} {{ item.message ? ' - ' + item.message : '' }}
            </div>
            <button 
              v-if="item"
              type="button" 
              class="mt-1 text-xs text-blue-600 hover:text-blue-800 focus:outline-none" 
              @click="openAlertModal(item)"
            >
              显示详情
            </button>
          </template>
          
          <!-- 自定义渲染状态列 -->
          <template #cell-status="{ item }">
            <span v-if="item && item.status !== undefined" :class="getStatusClasses(item.status)" class="px-2 py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full">
              <i :class="getStatusIcon(item.status)" class="mr-1"></i>
              {{ getStatusText(item.status) }}
            </span>
            <span v-else class="px-2 py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full bg-gray-200 text-gray-900">
              <i class="fas fa-question-circle mr-1"></i>未知状态
            </span>
          </template>
          
          <!-- 自定义渲染操作列 -->
          <template #cell-actions="{ item }">
            <div v-if="item && item.status !== undefined" class="flex items-center space-x-2">
              <template v-if="item.status == 0">
                    <button 
                      type="button" 
                      class="alert-action-btn text-yellow-700 hover:text-yellow-900 bg-yellow-100 hover:bg-yellow-200 px-2 py-1 rounded border border-yellow-300" 
                      @click="acknowledgeAlert(item.id)"
                    >
                      <i class="fas fa-check-circle mr-1"></i>确认
                    </button>
                    <button 
                      type="button" 
                      class="alert-action-btn text-red-700 hover:text-red-900 bg-red-100 hover:bg-red-200 px-2 py-1 rounded border border-red-300"
                      @click="ignoreAlert(item.id)"
                    >
                      <i class="fas fa-ban mr-1"></i>忽略
                    </button>
                  </template>
                  
                  <!-- 已通知状态 -->
              <template v-else-if="item.status == 1">
                    <button 
                      type="button" 
                      class="alert-action-btn text-yellow-700 hover:text-yellow-900 bg-yellow-100 hover:bg-yellow-200 px-2 py-1 rounded border border-yellow-300"
                      @click="acknowledgeAlert(item.id)"
                    >
                      <i class="fas fa-check-circle mr-1"></i>确认
                    </button>
                    <button 
                      type="button" 
                      class="alert-action-btn text-red-700 hover:text-red-900 bg-red-100 hover:bg-red-200 px-2 py-1 rounded border border-red-300"
                      @click="ignoreAlert(item.id)"
                    >
                      <i class="fas fa-ban mr-1"></i>忽略
                    </button>
                  </template>
                  
                  <!-- 已确认状态 -->
              <template v-else-if="item.status == 2">
                    <button 
                      type="button" 
                      class="alert-action-btn text-green-700 hover:text-green-900 bg-green-100 hover:bg-green-200 px-2 py-1 rounded border border-green-300"
                      @click="resolveAlert(item.id)"
                    >
                      <i class="fas fa-check-double mr-1"></i>解决
                    </button>
                    <button 
                      type="button" 
                      class="alert-action-btn text-red-700 hover:text-red-900 bg-red-100 hover:bg-red-200 px-2 py-1 rounded border border-red-300"
                      @click="ignoreAlert(item.id)"
                    >
                      <i class="fas fa-ban mr-1"></i>忽略
                    </button>
                  </template>
                  
                  <!-- 已解决或已忽略状态 -->
              <template v-else-if="item.status == 3 || item.status == 4">
                    <button 
                      type="button" 
                      class="alert-action-btn text-blue-700 hover:text-blue-900 bg-blue-100 hover:bg-blue-200 px-2 py-1 rounded border border-blue-300"
                      @click="reopenAlert(item.id)"
                    >
                      <i class="fas fa-redo mr-1"></i>重新打开
                    </button>
                  </template>
            </div>
            <div v-else>-</div>
          </template>
        </TableList>
      </Card>
    </div>
    
    <!-- 警报详情模态框 -->
    <Modal 
      :visible="showModal" 
      title="警报详情"
      maxWidth="2xl"
      @close="closeAlertModal"
    >
      <div v-if="selectedAlert" class="px-6 py-4">
        <div class="mb-4">
          <p class="text-sm text-gray-500 mb-1">警报ID</p>
          <p class="font-medium text-gray-900">{{ selectedAlert.id }}</p>
        </div>
        <div class="mb-4">
          <p class="text-sm text-gray-500 mb-1">发生时间</p>
          <p class="font-medium text-gray-900">{{ formatDateTime(selectedAlert.created_at) }}</p>
        </div>
        <div class="mb-4">
          <p class="text-sm text-gray-500 mb-1">警报类型</p>
          <p class="font-medium text-gray-900">{{ selectedAlert.source }}</p>
        </div>
        <div class="mb-4">
          <p class="text-sm text-gray-500 mb-1">分类</p>
          <p class="font-medium text-gray-900">{{ selectedAlert.category || '系统' }}</p>
        </div>
        <div class="mb-4">
          <p class="text-sm text-gray-500 mb-1">严重程度</p>
          <p class="font-medium text-gray-900">{{ getSeverityText(selectedAlert.severity) }}</p>
        </div>
        <div class="mb-4">
          <p class="text-sm text-gray-500 mb-1">警报内容</p>
          <div class="font-medium text-gray-900">{{ selectedAlert.title }} {{ selectedAlert.message ? ' - ' + selectedAlert.message : '' }}</div>
        </div>
      </div>
      <template #footer>
        <div class="flex justify-between w-full">
          <div class="flex space-x-3 justify-start">
            <!-- 当前选定警报的可用操作 -->
            <template v-if="selectedAlert">
              <!-- 未处理或已通知状态 -->
              <template v-if="selectedAlert.status == 0 || selectedAlert.status == 1">
                <button 
                  type="button" 
                  class="bg-yellow-100 border border-yellow-300 text-yellow-700 rounded-md py-2 px-4 text-sm font-medium hover:bg-yellow-200"
                  @click="acknowledgeAlert(selectedAlert.id)"
                >
                  <i class="fas fa-check-circle mr-1"></i>确认
                </button>
                <button 
                  type="button" 
                  class="bg-red-100 border border-red-300 text-red-700 rounded-md py-2 px-4 text-sm font-medium hover:bg-red-200"
                  @click="ignoreAlert(selectedAlert.id)"
                >
                  <i class="fas fa-ban mr-1"></i>忽略
                </button>
              </template>
              
              <!-- 已确认状态 -->
              <template v-else-if="selectedAlert.status == 2">
                <button 
                  type="button" 
                  class="bg-green-100 border border-green-300 text-green-700 rounded-md py-2 px-4 text-sm font-medium hover:bg-green-200"
                  @click="resolveAlert(selectedAlert.id)"
                >
                  <i class="fas fa-check-double mr-1"></i>解决
                </button>
                <button 
                  type="button" 
                  class="bg-red-100 border border-red-300 text-red-700 rounded-md py-2 px-4 text-sm font-medium hover:bg-red-200"
                  @click="ignoreAlert(selectedAlert.id)"
                >
                  <i class="fas fa-ban mr-1"></i>忽略
                </button>
              </template>
              
              <!-- 已解决或已忽略状态 -->
              <template v-else-if="selectedAlert.status == 3 || selectedAlert.status == 4">
                <button 
                  type="button" 
                  class="bg-blue-100 border border-blue-300 text-blue-700 rounded-md py-2 px-4 text-sm font-medium hover:bg-blue-200"
                  @click="reopenAlert(selectedAlert.id)"
                >
                  <i class="fas fa-redo mr-1"></i>重新打开
                </button>
              </template>
            </template>
          </div>
          <button 
            type="button" 
            class="bg-gray-100 border border-gray-300 rounded-md py-2 px-4 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" 
            @click="closeAlertModal"
          >
            关闭
          </button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import { ref, reactive, onMounted, nextTick } from 'vue';
import { format } from 'date-fns';
import Chart from 'chart.js/auto';
import PageHeader from '@/components/common/PageHeader.vue';
import Card from '@/components/common/Card.vue';
import Modal from '@/components/common/Modal.vue';
import FormSelect from '@/components/common/FormSelect.vue';
import FormInput from '@/components/common/FormInput.vue';
import TableList from '@/components/common/TableList.vue';
import SearchFilters from '@/components/common/SearchFilters.vue';
import AdminApiClient from '@/utils/admin-api-client';

export default {
  name: 'SystemAlerts',
  components: {
    PageHeader,
    Card,
    Modal,
    FormSelect,
    FormInput,
    TableList,
    SearchFilters
  },
  setup() {
    // 初始化API客户端
    const apiClient = new AdminApiClient();

      // 警报概览数据
    const activeCount = ref(0);
    const activeCountDiff = ref('');
    const criticalCount = ref(0);
    const criticalCountDiff = ref('');
    const resolvedCount = ref(0);
    const resolvedCountWeek = ref('');
    const avgResponseTime = ref('');
    const avgResponseTimeDiff = ref('');
      
      // 筛选器
    const filters = reactive({
        status: '',
        severity: '',
        source: '',
        keyword: '',
        category: '',
        date_range: format(new Date(), 'yyyy-MM-dd') + ' 至 ' + format(new Date(), 'yyyy-MM-dd')
    });
    
    // 筛选器配置
    const filterConfig = [
      {
        id: 'status',
        type: 'select',
        label: '警报状态',
        icon: 'fas fa-bell',
        options: [
          { value: '', label: '全部状态' },
          { value: '0', label: '未处理' },
          { value: '1', label: '已通知' },
          { value: '2', label: '已确认' },
          { value: '3', label: '已解决' },
          { value: '4', label: '已忽略' },
          { value: '5', label: '通知失败' }
        ]
      },
      {
        id: 'severity',
        type: 'select',
        label: '严重程度',
        icon: 'fas fa-exclamation-triangle',
        options: [
          { value: '', label: '全部等级' },
          { value: 'p0', label: '严重(P0)' },
          { value: 'p1', label: '高(P1)' },
          { value: 'p2', label: '中高(P2)' },
          { value: 'p3', label: '中(P3)' },
          { value: 'p4', label: '低(P4)' }
        ]
      },
      {
        id: 'source',
        type: 'select',
        label: '警报类型',
        icon: 'fas fa-tags',
        options: [
          { value: '', label: '全部类型' }
          // 动态添加选项
        ]
      },
      {
        id: 'keyword',
        type: 'input',
        label: '关键词',
        placeholder: '搜索警报内容',
        icon: 'fas fa-search',
        inputType: 'text'
      },
      {
        id: 'date_range',
        type: 'dateRange',
        label: '日期范围',
        placeholder: '选择日期范围',
        icon: 'fas fa-calendar',
        dateFormat: 'Y-m-d',
        rangeDelimiter: ' 至 '
      },
      {
        id: 'category',
        type: 'select',
        label: '分类',
        icon: 'fas fa-folder',
        options: [
          { value: '', label: '全部分类' }
          // 动态添加选项
        ]
      }
    ];
      
      // 选项数据
    const sources = ref([]);
    const categories = ref([]);
      
      // 状态选项
    const statusOptions = [
        { value: '', label: '全部状态' },
        { value: '0', label: '未处理' },
        { value: '1', label: '已通知' },
        { value: '2', label: '已确认' },
        { value: '3', label: '已解决' },
        { value: '4', label: '已忽略' },
        { value: '5', label: '通知失败' }
    ];
      
      // 严重程度选项
    const severityOptions = [
        { value: '', label: '全部等级' },
        { value: 'p0', label: '严重(P0)' },
        { value: 'p1', label: '高(P1)' },
        { value: 'p2', label: '中高(P2)' },
        { value: 'p3', label: '中(P3)' },
        { value: 'p4', label: '低(P4)' }
    ];
    
    // 表格列定义
    const alertColumns = [
      { key: 'id', label: 'ID' },
      { key: 'created_at', label: '时间', formatter: (value) => formatDateTime(value) },
      { 
        key: 'severity', 
        label: '严重程度',
        customRender: true
      },
      { key: 'category', label: '分类', defaultValue: '系统' },
      { key: 'source', label: '警报类型' },
      { 
        key: 'content', 
        label: '警报内容', 
        width: 'max-w-xs',
        customRender: true
      },
      { 
        key: 'status', 
        label: '状态',
        customRender: true
      },
      { 
        key: 'actions', 
        label: '操作',
        customRender: true
      }
    ];
    
    // 图表数据
    const showingSeverityTrend = ref(true);
    let severityTrendChart = null;
    let statusTrendChart = null;
    const alertsTrendChart = ref(null);
    const statusTrendChart_ref = ref(null);
    
    // 实际图表数据
    const statusTrendData = {
      dates: ["04-15", "04-16", "04-17", "04-18", "04-19", "04-20", "04-21"],
      series: [
        {name: "未处理", color: "#FF9800", data: [6, 8, 9, 8, 8, 8, 8]},
        {name: "已处理", color: "#4CAF50", data: [10, 12, 8, 9, 10, 12, 10]},
        {name: "已忽略", color: "#9E9E9E", data: [2, 1, 3, 2, 3, 2, 2]}
      ]
    };
    
    const severityTrendData = {
      dates: ["04-15", "04-16", "04-17", "04-18", "04-19", "04-20", "04-21"],
      series: [
        {name: "严重", color: "#FF5252", data: [1, 1, 3, 3, 3, 1, 1]},
        {name: "高", color: "#FF9800", data: [5, 4, 4, 3, 5, 6, 5]},
        {name: "中", color: "#2196F3", data: [8, 12, 9, 10, 9, 11, 10]},
        {name: "低", color: "#4CAF50", data: [4, 4, 4, 3, 4, 4, 4]}
      ]
    };
    
    // 警报列表数据
    const loading = ref(true);
    const alerts = ref([]);
    const totalItems = ref(0);
    const currentPage = ref(1);
    const itemsPerPage = ref(15);
    
    // 警报详情模态框
    const showModal = ref(false);
    const selectedAlert = ref(null);
    
    // 筛选器操作
    const resetFilters = () => {
      Object.keys(filters).forEach(key => {
        filters[key] = '';
      });
      currentPage.value = 1;
      loadAlerts();
    };
    
    const searchAlerts = (newFilters) => {
      Object.assign(filters, newFilters);
      currentPage.value = 1;
      loading.value = true; // Set loading state when searching
      loadAlerts();
    };
    
    // 图表操作
    const showSeverityTrend = () => {
      showingSeverityTrend.value = true;
    };
    
    const showStatusTrend = () => {
      showingSeverityTrend.value = false;
    };
    
    const downloadChart = () => {
      const chart = showingSeverityTrend.value ? severityTrendChart : statusTrendChart;
      if (!chart) return;
      
      const canvas = chart.canvas;
      const dataUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = '警报趋势图-' + new Date().getTime() + '.png';
      link.href = dataUrl;
      link.click();
    };
    
    // 图表初始化
    const initSeverityTrendChart = () => {
      if (!alertsTrendChart.value) {
        console.warn('Severity trend chart canvas element is not available');
        return;
      }
      
      const ctx = alertsTrendChart.value.getContext('2d');
      if (!ctx) {
        console.warn('Could not get 2D context from canvas');
        return;
      }
      
      const labels = severityTrendData.dates;
      const datasets = severityTrendData.series.map(series => {
        return {
          label: series.name,
          backgroundColor: `${series.color}80`, // 添加50%透明度
          borderColor: series.color,
          borderWidth: 2,
          data: series.data
        };
      });
      
      severityTrendChart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels,
          datasets
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              stacked: true,
              grid: {
                display: false
              }
            },
            y: {
              stacked: true,
              beginAtZero: true,
              ticks: {
                precision: 0
              }
            }
          },
          plugins: {
            legend: {
              position: 'top'
            },
            tooltip: {
              mode: 'index'
            }
          }
        }
      });
    };
    
    const initStatusTrendChart = () => {
      if (!statusTrendChart_ref.value) {
        console.warn('Status trend chart canvas element is not available');
        return;
      }
      
      const ctx = statusTrendChart_ref.value.getContext('2d');
      if (!ctx) {
        console.warn('Could not get 2D context from canvas');
        return;
      }
      
      const labels = statusTrendData.dates;
      const datasets = statusTrendData.series.map(series => {
        return {
          label: series.name,
          backgroundColor: `${series.color}20`, // 添加12%透明度
          borderColor: series.color,
          borderWidth: 2,
          data: series.data
        };
      });
      
      statusTrendChart = new Chart(ctx, {
        type: 'line',
        data: {
          labels,
          datasets
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                precision: 0
              }
            }
          },
          plugins: {
            legend: {
              position: 'top'
            },
            tooltip: {
              mode: 'index'
            }
          },
          elements: {
            line: {
              tension: 0.3
            }
          }
        }
      });
    };
    
    // 加载警报数据
    const loadAlerts = async () => {
      loading.value = true;
      
      try {
        // 构建API请求参数
        const params = {
          page: currentPage.value,
          per_page: itemsPerPage.value,
          ...filters
        };
        
        const response = await apiClient.getSystemAlerts(params);
        
        if (response && response.success) {
          // 处理警报数据
          alerts.value = response.data.alerts.data || [];
          totalItems.value = response.data.alerts.total;
          currentPage.value = response.data.alerts.current_page;
          itemsPerPage.value = response.data.alerts.per_page;
          
          // 更新图表数据
          if (response.data.statusTrendData) {
            Object.assign(statusTrendData, response.data.statusTrendData);
            if (statusTrendChart) {
              statusTrendChart.data.labels = statusTrendData.dates;
              statusTrendChart.data.datasets = statusTrendData.series.map(series => ({
                label: series.name,
                backgroundColor: `${series.color}20`,
                borderColor: series.color,
                borderWidth: 2,
                data: series.data
              }));
              statusTrendChart.update();
            }
          }
          
          if (response.data.trendData) {
            Object.assign(severityTrendData, response.data.trendData);
            if (severityTrendChart) {
              severityTrendChart.data.labels = severityTrendData.dates;
              severityTrendChart.data.datasets = severityTrendData.series.map(series => ({
                label: series.name,
                backgroundColor: `${series.color}80`,
                borderColor: series.color,
                borderWidth: 2,
                data: series.data
              }));
              severityTrendChart.update();
            }
          }
          
          // 更新统计数据
          activeCount.value = response.data.activeCount || 0;
          activeCountDiff.value = response.data.activeCountDiff || '';
          criticalCount.value = response.data.criticalCount || 0;
          criticalCountDiff.value = response.data.criticalCountDiff || '';
          resolvedCount.value = response.data.resolvedCount || 0;
          resolvedCountWeek.value = response.data.resolvedCountWeek || '';
          avgResponseTime.value = response.data.avgResponseTime || '';
          avgResponseTimeDiff.value = response.data.avgResponseTimeDiff || '';
          
          // 更新选项数据
          if (response.data.sources && response.data.sources.length > 0) {
            sources.value = response.data.sources;
            // 更新筛选器选项 - 警报类型
            filterConfig[2].options = [
              { value: '', label: '全部类型' },
              ...response.data.sources.map(source => ({ value: source, label: source }))
            ];
          }
          
          if (response.data.categories && response.data.categories.length > 0) {
            categories.value = response.data.categories;
            // 更新筛选器选项 - 分类
            filterConfig[5].options = [
              { value: '', label: '全部分类' },
              ...response.data.categories.map(category => ({ value: category, label: category }))
            ];
          }
        } else {
          console.error('加载警报数据失败:', response?.message);
        }
      } catch (error) {
        console.error('加载警报数据异常:', error);
      } finally {
        loading.value = false;
      }
    };
    
    // 分页操作
    const goToPage = (page) => {
      currentPage.value = page;
      loadAlerts();
    };
  
    
    // 警报状态和严重程度处理
    const getSeverityText = (severity) => {
      if (!severity) return '未知';
      
      const map = {
        'p0': '严重(P0)',
        'p1': '高(P1)',
        'p2': '中高(P2)',
        'p3': '中(P3)',
        'p4': '低(P4)',
        'critical': '严重(P0)',
        'high': '高(P1)',
        'warning': '中高(P2)',
        'medium': '中(P3)',
        'info': '中(P3)',
        'low': '低(P4)'
      };
      return map[severity.toLowerCase()] || severity;
    };
    
    const getSeverityClasses = (severity) => {
      if (!severity) return 'bg-gray-200 text-gray-900 border-2 border-gray-400 shadow-sm';
      
      const map = {
        'p0': 'bg-red-200 text-red-900 border-2 border-red-500 shadow-md',
        'p1': 'bg-yellow-200 text-yellow-900 border-2 border-yellow-500 shadow-md',
        'p2': 'bg-orange-200 text-orange-900 border-2 border-orange-400 shadow-sm',
        'p3': 'bg-blue-200 text-blue-900 border-2 border-blue-400 shadow-sm',
        'p4': 'bg-green-200 text-green-900 border-2 border-green-400 shadow-sm',
        'critical': 'bg-red-200 text-red-900 border-2 border-red-500 shadow-md',
        'high': 'bg-yellow-200 text-yellow-900 border-2 border-yellow-500 shadow-md',
        'warning': 'bg-orange-200 text-orange-900 border-2 border-orange-400 shadow-sm',
        'medium': 'bg-blue-200 text-blue-900 border-2 border-blue-400 shadow-sm',
        'info': 'bg-blue-200 text-blue-900 border-2 border-blue-400 shadow-sm',
        'low': 'bg-green-200 text-green-900 border-2 border-green-400 shadow-sm'
      };
      
      return map[severity.toLowerCase()] || 'bg-gray-200 text-gray-900 border-2 border-gray-400 shadow-sm';
    };
    
    const getSeverityIcon = (severity) => {
      if (!severity) return 'fas fa-info-circle';
      
      const map = {
        'p0': 'fas fa-exclamation-circle',
        'p1': 'fas fa-exclamation-triangle',
        'p2': 'fas fa-exclamation',
        'p3': 'fas fa-info-circle',
        'p4': 'fas fa-check-circle',
        'critical': 'fas fa-exclamation-circle',
        'high': 'fas fa-exclamation-triangle',
        'warning': 'fas fa-exclamation',
        'medium': 'fas fa-info-circle',
        'info': 'fas fa-info-circle',
        'low': 'fas fa-check-circle'
      };
      return map[severity.toLowerCase()] || 'fas fa-info-circle';
    };
    
    const getStatusText = (status) => {
      const map = {
        0: '未处理',
        1: '已通知',
        2: '已确认',
        3: '已解决',
        4: '已忽略',
        5: '通知失败'
      };
      return map[status] || '未知状态';
    };
    
    const getStatusClasses = (status) => {
      const map = {
        0: 'bg-red-200 text-red-900 border-2 border-red-400 shadow-sm',
        1: 'bg-blue-200 text-blue-900 border-2 border-blue-400 shadow-sm',
        2: 'bg-yellow-200 text-yellow-900 border-2 border-yellow-400 shadow-sm',
        3: 'bg-green-200 text-green-900 border-2 border-green-400 shadow-sm',
        4: 'bg-gray-200 text-gray-900 border-2 border-gray-400 shadow-sm',
        5: 'bg-purple-200 text-purple-900 border-2 border-purple-400 shadow-sm'
      };
      return map[status] || 'bg-blue-200 text-blue-900 border-2 border-blue-400 shadow-sm';
    };
    
    const getStatusIcon = (status) => {
      const map = {
        0: 'fas fa-exclamation-circle',
        1: 'fas fa-bell',
        2: 'fas fa-user-check',
        3: 'fas fa-check-double',
        4: 'fas fa-ban',
        5: 'fas fa-times-circle'
      };
      return map[status] || '';
    };
    
    // 格式化时间
    const formatDateTime = (dateString) => {
      try {
        const date = new Date(dateString);
        return format(date, 'yyyy-MM-dd HH:mm:ss');
      } catch(e) {
        return dateString;
      }
    };
    
    // 警报操作
    const acknowledgeAlert = async (alertId) => {
      try {
        const response = await apiClient.post('/system/alerts/acknowledge', { id: alertId });
        
        if (response && response.success) {
          // 更新本地状态
          const alert = alerts.value.find(a => a.id === alertId);
      if (alert) {
        alert.status = 2; // 已确认
        
            if (selectedAlert.value && selectedAlert.value.id === alertId) {
              selectedAlert.value.status = 2;
            }
          }
        } else {
          console.error('确认警报失败:', response?.message);
        }
      } catch (error) {
        console.error('确认警报异常:', error);
      }
    };
    
    const ignoreAlert = async (alertId) => {
      try {
        const response = await apiClient.post('/system/alerts/ignore', { id: alertId });
        
        if (response && response.success) {
          // 更新本地状态
          const alert = alerts.value.find(a => a.id === alertId);
      if (alert) {
        alert.status = 4; // 已忽略
        
            if (selectedAlert.value && selectedAlert.value.id === alertId) {
              selectedAlert.value.status = 4;
            }
          }
        } else {
          console.error('忽略警报失败:', response?.message);
        }
      } catch (error) {
        console.error('忽略警报异常:', error);
      }
    };
    
    const resolveAlert = async (alertId) => {
      try {
        const response = await apiClient.post('/system/alerts/resolve', { id: alertId });
        
        if (response && response.success) {
          // 更新本地状态
          const alert = alerts.value.find(a => a.id === alertId);
      if (alert) {
        alert.status = 3; // 已解决
        
            if (selectedAlert.value && selectedAlert.value.id === alertId) {
              selectedAlert.value.status = 3;
            }
          }
        } else {
          console.error('解决警报失败:', response?.message);
        }
      } catch (error) {
        console.error('解决警报异常:', error);
      }
    };
    
    const reopenAlert = async (alertId) => {
      try {
        const response = await apiClient.post('/system/alerts/reopen', { id: alertId });
        
        if (response && response.success) {
          // 更新本地状态
          const alert = alerts.value.find(a => a.id === alertId);
      if (alert) {
        alert.status = 0; // 未处理
        
            if (selectedAlert.value && selectedAlert.value.id === alertId) {
              selectedAlert.value.status = 0;
            }
          }
        } else {
          console.error('重新打开警报失败:', response?.message);
        }
      } catch (error) {
        console.error('重新打开警报异常:', error);
      }
    };
    
    const pauseAllAlerts = async () => {
      try {
        const response = await apiClient.post('/system/alerts/pause-all');
        
        if (response && response.success) {
      alert('系统警报已全部暂停，可以在设置中重新启用。');
        } else {
          console.error('暂停所有警报失败:', response?.message);
        }
      } catch (error) {
        console.error('暂停所有警报异常:', error);
      }
    };
    
    // 警报详情模态框
    const openAlertModal = (alert) => {
      selectedAlert.value = { ...alert };
      showModal.value = true;
    };
    
    const closeAlertModal = () => {
      showModal.value = false;
      selectedAlert.value = null;
    };
    
    // 组件挂载时执行
    onMounted(() => {
      // 加载警报数据
      loadAlerts();
      
      // 确保在DOM更新后初始化图表
      nextTick(() => {
        // 尝试初始化图表，如果失败，等待一会再试
        try {
          if (alertsTrendChart.value && statusTrendChart_ref.value) {
            initSeverityTrendChart();
            initStatusTrendChart();
          } else {
            setTimeout(() => {
              initSeverityTrendChart();
              initStatusTrendChart();
            }, 500);
          }
        } catch (error) {
          console.error('初始化图表错误:', error);
        }
      });
    });
    
    return {
      // 变量
      activeCount,
      activeCountDiff,
      criticalCount,
      criticalCountDiff,
      resolvedCount,
      resolvedCountWeek,
      avgResponseTime,
      avgResponseTimeDiff,
      filters,
      filterConfig,
      sources,
      categories,
      alertColumns,
      showingSeverityTrend,
      loading,
      alerts,
      showModal,
      selectedAlert,
      statusOptions,
      severityOptions,
      alertsTrendChart,
      statusTrendChart_ref,
      statusTrendData,
      severityTrendData,
      totalItems,
      currentPage,
      itemsPerPage,
    
      // 方法
      resetFilters,
      searchAlerts,
      showSeverityTrend,
      showStatusTrend,
      downloadChart,
      goToPage,
      getSeverityText,
      getSeverityClasses,
      getSeverityIcon,
      getStatusText,
      getStatusClasses,
      getStatusIcon,
      formatDateTime,
      acknowledgeAlert,
      ignoreAlert,
      resolveAlert,
      reopenAlert,
      pauseAllAlerts,
      openAlertModal,
      closeAlertModal
    };
  }
};
</script>

<style scoped>
/* 图表切换动画 */
.fade-in {
    animation: fadeIn 0.5s ease-in-out;
}

@keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
}

/* 图表悬停效果 */
.chart-container:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transition: box-shadow 0.3s ease;
}

/* 修复Chart.js白色圆点问题 */
canvas {
    -webkit-backface-visibility: hidden;
    -moz-backface-visibility: hidden;
    -webkit-transform: translate3d(0, 0, 0);
    -moz-transform: translate3d(0, 0, 0);
}

table {
    min-width: 100%;
    table-layout: auto;
}

th {
    min-width: 120px;
    white-space: nowrap;
}

th.wider-column {
    min-width: 180px;
}

@media (max-width: 768px) {
    th, td {
        min-width: 100px;
    }
}

.loader {
  border: 3px solid #f3f3f3;
  border-radius: 50%;
  border-top: 3px solid #3498db;
  width: 30px;
  height: 30px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>

